import firebase from "firebase";
import _ from "lodash";
import {Cancellation, CreateOrderData, FeOrder, FsOrder, UpdateOrderInput, UpdateOrderResult} from "./types";
import {replaceUndefinedWithDelete} from "../../../utils";

const orderDataConverter: firebase.firestore.FirestoreDataConverter<FeOrder> = {
    fromFirestore(snapshot: firebase.firestore.QueryDocumentSnapshot<FsOrder>): FeOrder {
        const {createdAt, updatedAt, ...docData} = snapshot.data();
        return {
            id: snapshot.id,
            ...docData,
            createdAt: createdAt.toMillis(),
            updatedAt: updatedAt.toMillis()
        }
    },
    toFirestore({id, createdAt, updatedAt, ...docData}: FeOrder): FsOrder {
        return {
            ..._.pickBy(docData, _.identity) as FeOrder,
            createdAt: firebase.firestore.Timestamp.fromMillis(createdAt),
            updatedAt: firebase.firestore.Timestamp.fromMillis(updatedAt),
        };
    }
}

export class OrdersService {
    private static GetCollection() {
        return firebase.firestore()
            .collection('orders')
            .withConverter(orderDataConverter);
    }

    async createOrder(dto: CreateOrderData): Promise<FeOrder> {
        // const result = await OrdersService.GetCollection().add({
        //     id: '',
        //     ...dto,
        //     confirmed: true,
        //     createdAt: dateInMillis,
        //     updatedAt: dateInMillis,
        // });
        // return {
        //     ...dto,
        //     id: result.id,
        //     confirmed: true,
        //     createdAt: dateInMillis,
        //     updatedAt: dateInMillis,
        // }
        const result = await firebase.app()
            .functions('europe-west3')
            .httpsCallable('createOrder')({
                ...dto,
                confirmed: true,
                createdAt: firebase.firestore.Timestamp.fromDate(new Date()),
                updatedAt: firebase.firestore.Timestamp.fromDate(new Date())
            });

        return result.data;
    }

    async updateOrder(id: string, dto: UpdateOrderInput): Promise<UpdateOrderResult> {
        await OrdersService.GetCollection().doc(id).update({
            ...replaceUndefinedWithDelete(dto),
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
        return {
            id,
            updatedAt: new Date().getTime(),
            ...dto,
        }
    }

    async listOrders(): Promise<FeOrder[]> {
        const querySnapshots = await OrdersService.GetCollection()
            .orderBy("estimatedFrom", "desc")
            .get();
        return querySnapshots.empty
            ? []
            : querySnapshots.docs.map(d => d.data())
    }

    async listClientOrders(clientId: string): Promise<FeOrder[]> {
        const querySnapshots = await OrdersService.GetCollection()
            .where('clientId', '==', clientId)
            .orderBy("estimatedFrom", "desc")
            .get();
        return querySnapshots.empty
            ? []
            : querySnapshots.docs.map(d => d.data())
    }

    async listWorkerOrders(workerId: string, timeRange?: { start: number, end: number }): Promise<FeOrder[]> {
        let querySnapshots;
        if (timeRange) {
            querySnapshots = await OrdersService.GetCollection()
                .where('workerId', '==', workerId)
                .where('estimatedFrom', '>=', timeRange.start)
                .where('estimatedFrom', '<=', timeRange.end)
                .orderBy("estimatedFrom", "desc")
                .get()
        } else {
            querySnapshots = await OrdersService.GetCollection()
                .where('workerId', '==', workerId)
                .get();
        }

        return querySnapshots.empty
            ? []
            : querySnapshots.docs.map(d => d.data())
    }

    async getOrder(id: string): Promise<FeOrder> {
        const snapshot = await OrdersService.GetCollection().doc(id).get();
        const worker = snapshot.data();
        if (!worker) {
            throw new Error('Order not found');
        }
        return worker;
    }

    async getLastOrderOfClient(clientId: string): Promise<FeOrder | undefined> {
        const res = await OrdersService.GetCollection()
            .where("clientId", "==", clientId)
            .orderBy("estimatedFrom", "desc")
            .limit(1)
            .get();

        return res.docs[0]?.data()
    }

    async addOrderService(workerId: string, serviceId: string) {
        await OrdersService.GetCollection()
            .doc(workerId)
            .update({
                servicesIds: firebase.firestore.FieldValue.arrayUnion(serviceId),
                updatedAt: firebase.firestore.FieldValue.serverTimestamp()
            });

        return {
            workerId,
            serviceId,
            updatedAt: new Date().getTime()
        }
    }

    async cancelOrder(dto: { orderId: string; cancellation: Cancellation }): Promise<FeOrder> {
        const result = await firebase.app()
            .functions('europe-west3')
            .httpsCallable('cancelOrder')(dto);

        return result.data;
    }
}



